import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Label,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Tab,
  TabList,
  makeStyles,
} from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';

import { TenantRoleFilter, TenantSummary, getTenants } from '../api/tenantRegistry';
import ConsentStatusChip from '../components/ConsentStatusChip';
import { DataTable, DataTableColumn } from '../components/DataTable';
import RelativeTime from '../components/RelativeTime';
import TenantLabel from '../components/TenantLabel';
import { HeroBand } from '../components/abPrimitives';
import { TEXT } from '../theme/tokens';

type RoleFilterValue = 'all' | TenantRoleFilter;

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '24px',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    columnGap: '24px',
    flexWrap: 'wrap',
    rowGap: '12px',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '4px',
  },
  tableSurface: {
    backgroundColor: '#ffffff',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: '#e4e4e7',
    borderRightColor: '#e4e4e7',
    borderBottomColor: '#e4e4e7',
    borderLeftColor: '#e4e4e7',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
    overflowX: 'auto',
  },
});

// Capitalizes a role string for display without assuming the server's exact
// casing (source/destination). Purely presentational.
function titleCaseRole(role: string): string {
  if (!role) return role;
  return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
}

export default function TenantsListPage() {
  const styles = useStyles();
  const navigate = useNavigate();

  const [roleFilter, setRoleFilter] = useState<RoleFilterValue>('all');
  const roleParam = roleFilter === 'all' ? undefined : roleFilter;

  const query = useQuery<TenantSummary[]>({
    queryKey: ['tenants', { role: roleParam }],
    queryFn: () => getTenants(roleParam),
  });

  const rows = useMemo<TenantSummary[]>(() => query.data ?? [], [query.data]);

  const columns = useMemo<DataTableColumn<TenantSummary>[]>(
    () => [
      {
        key: 'tenant',
        header: 'Tenant',
        render: (t) => (
          <span className="font-medium" style={{ color: TEXT.primary }}>
            {t.displayName?.trim() || t.tenantDomain?.trim() ? (
              t.displayName?.trim() || t.tenantDomain?.trim()
            ) : (
              <TenantLabel domain={t.tenantDomain} tenantId={t.tenantId} />
            )}
          </span>
        ),
      },
      {
        key: 'domain',
        header: 'Domain',
        render: (t) =>
          t.tenantDomain?.trim() ? (
            <TenantLabel domain={t.tenantDomain} tenantId={t.tenantId} />
          ) : (
            <span style={{ color: TEXT.disabled }}>—</span>
          ),
      },
      {
        key: 'roles',
        header: 'Role',
        render: (t) =>
          t.roles.length > 0 ? (
            <span style={{ color: TEXT.secondary }}>
              {t.roles.map(titleCaseRole).join(' · ')}
            </span>
          ) : (
            <span style={{ color: TEXT.disabled }}>—</span>
          ),
      },
      {
        key: 'consentStatus',
        header: 'Consent',
        render: (t) => <ConsentStatusChip status={t.consentStatus} />,
      },
      {
        key: 'linkedMigrationCount',
        header: 'Migrations',
        numeric: true,
        align: 'right',
        render: (t) => <span>{t.linkedMigrationCount.toLocaleString()}</span>,
      },
      {
        key: 'refreshedUtc',
        header: 'Checked',
        numeric: true,
        render: (t) => <RelativeTime iso={t.refreshedUtc} emptyPlaceholder="never" />,
      },
    ],
    [],
  );

  const errorNode = query.isError ? (
    <MessageBar intent="error">
      <MessageBarBody>
        <MessageBarTitle>Could not load tenants</MessageBarTitle>
        {errorMessage(query.error)}
      </MessageBarBody>
      <MessageBarActions>
        <Button
          size="small"
          appearance="subtle"
          icon={<RefreshCw size={14} strokeWidth={1.5} />}
          onClick={() => query.refetch()}
        >
          Retry
        </Button>
      </MessageBarActions>
    </MessageBar>
  ) : undefined;

  const emptyNode = (
    <MessageBar intent="info">
      <MessageBarBody>
        <MessageBarTitle>No tenants yet</MessageBarTitle>
        {roleParam
          ? `No ${roleParam} tenants are registered.`
          : 'Connect the tenants this environment migrates from and to. A campaign that names a tenant also adds it here.'}
      </MessageBarBody>
      <MessageBarActions>
        <Button appearance="primary" onClick={() => navigate('/tenants/connect')}>
          Connect a tenant
        </Button>
      </MessageBarActions>
    </MessageBar>
  );

  return (
    <div className={styles.page}>
      <HeroBand
        eyebrow="Resources"
        title="Tenants"
        subtitle="Every Entra tenant connected to this deployment — consent state, service principals, minting accounts, and wave credentials."
      />

      <div className={styles.header}>

        <div className={styles.filterGroup}>
          <Label weight="semibold" size="small">
            Filter by role
          </Label>
          <TabList
            selectedValue={roleFilter}
            onTabSelect={(_, data) => setRoleFilter(data.value as RoleFilterValue)}
            size="small"
          >
            <Tab value="all">All</Tab>
            <Tab value="source">Source</Tab>
            <Tab value="destination">Destination</Tab>
          </TabList>
        </div>
      </div>

      <div className={styles.tableSurface}>
        <DataTable
          columns={columns}
          data={rows}
          keyForRow={(t) => t.tenantId}
          onRowClick={(t) => navigate(`/tenants/${encodeURIComponent(t.tenantId)}/overview`)}
          isLoading={query.isLoading}
          error={errorNode}
          emptyState={emptyNode}
        />
      </div>
    </div>
  );
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return 'Unexpected error';
}
